import React, { useMemo, useState } from "react";
import { fmtNum, positionColor } from "@/lib/format";
import { SignInRequired } from "@/components/SignInRequired";
import { Search, Sparkles, Target, X } from "lucide-react";

const POSITIONS = ["ANY", "QB", "RB", "WR", "TE", "PICK"];
const DEFAULT_VALUE_RANGE = 400;

export function TradeFinder({ isGuest = false, onSignIn, leagueDetail, onJumpToTradeCalculator }) {
  const myTeam = useMemo(
    () => leagueDetail?.rosters?.find((r) => r.is_me) || null,
    [leagueDetail]
  );
  const opponentRosters = useMemo(
    () => (leagueDetail?.rosters || []).filter((r) => !r.is_me),
    [leagueDetail]
  );

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [position, setPosition] = useState("ANY");
  const [valueRange, setValueRange] = useState(DEFAULT_VALUE_RANGE);

  const rosterAssets = useMemo(() => (
    (myTeam?.players || [])
      .map((asset, index) => ({
        ...asset,
        finderKey: `${index}-${String(asset.id || asset.name).replace(/[^a-zA-Z0-9_-]/g, "_")}`,
      }))
      .sort((a, b) => (b.combinedValue || 0) - (a.combinedValue || 0))
  ), [myTeam]);

  const assetByKey = useMemo(() => {
    const map = new Map();
    for (const asset of rosterAssets) map.set(asset.finderKey, asset);
    return map;
  }, [rosterAssets]);

  const selectedAssets = useMemo(
    () => selectedKeys.map((key) => assetByKey.get(key)).filter(Boolean),
    [assetByKey, selectedKeys]
  );

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const packageValue = selectedAssets.reduce((sum, asset) => sum + (asset.combinedValue || 0), 0);
  const hasPackage = selectedAssets.length > 0 && packageValue > 0;

  const toggleAsset = (asset) => {
    const key = asset.finderKey;
    setSelectedKeys((prev) => (
      prev.includes(key) ? prev.filter((itemKey) => itemKey !== key) : [...prev, key]
    ));
  };

  const removeAsset = (assetKey) => {
    setSelectedKeys((prev) => prev.filter((key) => key !== assetKey));
  };

  const clearAll = () => {
    setSelectedKeys([]);
    setPosition("ANY");
    setValueRange(DEFAULT_VALUE_RANGE);
  };

  const results = useMemo(() => {
    if (!hasPackage || !opponentRosters.length) return [];

    const out = [];
    for (const roster of opponentRosters) {
      for (const [index, player] of roster.players.entries()) {
        const value = Number(player.combinedValue || 0);
        if (!value) continue;
        if (position !== "ANY" && player.position !== position) continue;

        const delta = value - packageValue;
        if (Math.abs(delta) <= valueRange) {
          out.push({ roster, player, value, delta, resultKey: `${roster.roster_id}-${index}-${player.id}` });
        }
      }
    }

    return out
      .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta) || b.value - a.value)
      .slice(0, 24);
  }, [hasPackage, opponentRosters, packageValue, position, valueRange]);

  if (isGuest) {
    return (
      <section className="mt-16" data-testid="trade-finder-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">02 / Trade Finder</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
              Build a <span className="text-[#7C3AED]">package</span>.
            </h2>
            <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
              Select players and picks from your roster, then scan opponent rosters for matching targets within your custom value range.
            </p>
          </div>
        </div>
        <SignInRequired
          description="Sign in with your Sleeper username to load your roster and find trade targets across your league."
          onSignIn={onSignIn}
          testId="guest-finder-cta"
        />
      </section>
    );
  }

  if (!leagueDetail || !myTeam) {
    return (
      <section className="mt-16" data-testid="trade-finder-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">02 / Trade Finder</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
              Build a <span className="text-[#7C3AED]">package</span>.
            </h2>
            <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
              Select players and picks from your roster, then scan opponent rosters for matching targets within your custom value range.
            </p>
          </div>
        </div>
        <div className="brutal-card p-6 text-sm text-[#52525B]" data-testid="finder-no-league">
          Select a league from the header to load your roster and scan for trade targets.
        </div>
      </section>
    );
  }

  return (
    <section className="mt-16" data-testid="trade-finder-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">02 / Trade Finder</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
            Build a <span className="text-[#7C3AED]">package</span>.
          </h2>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            Select players and picks from your roster, then scan opponent rosters for matching targets within your custom value range.
          </p>
        </div>
        <button
          onClick={clearAll}
          disabled={selectedAssets.length === 0 && position === "ANY" && valueRange === DEFAULT_VALUE_RANGE}
          className="brutal-btn-outline px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
          data-testid="finder-clear-all"
        >
          Clear All
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        <div className="brutal-card flex flex-col" data-testid="finder-package-builder">
          <div className="border-b border-[#0A0A0A] px-4 py-3 flex items-center justify-between gap-2">
            <span className="label-eyebrow">Your Roster · {myTeam.team_name}</span>
            <span className="font-mono-data text-xs text-[#52525B]">
              {selectedAssets.length} {selectedAssets.length === 1 ? "asset" : "assets"}
            </span>
          </div>

          <div className="p-4 border-b border-[#0A0A0A]/15 bg-[#FAFAFA]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-eyebrow">Selected Total</div>
                <div className="text-[11px] text-[#52525B] font-mono-data mt-1">
                  This value drives the target search.
                </div>
              </div>
              <div className="font-display font-black text-4xl tabular-nums" data-testid="finder-package-total">
                {fmtNum(packageValue)}
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <label className="label-eyebrow">Search Range</label>
                <span className="font-mono-data text-xs text-[#52525B] tabular-nums">±{fmtNum(valueRange)}</span>
              </div>
              <input
                type="range"
                min="100"
                max="1500"
                step="50"
                value={valueRange}
                onChange={(e) => setValueRange(Number(e.target.value))}
                className="w-full accent-[#7C3AED]"
                data-testid="finder-range"
              />
            </div>
          </div>

          <div className="p-4 border-b border-[#0A0A0A]/15">
            {selectedAssets.length === 0 ? (
              <div className="text-xs text-[#52525B] py-4 text-center border border-dashed border-[#0A0A0A]/30">
                Select assets below to build your outgoing package.
              </div>
            ) : (
              <div className="space-y-2" data-testid="finder-selected-assets">
                {selectedAssets.map((asset) => (
                  <div key={asset.finderKey} className="flex items-center justify-between gap-3 border-l-4 border-l-[#7C3AED] border-y border-r border-[#0A0A0A] bg-[#F5F0FF] px-3 py-2 shadow-[2px_2px_0_0_#0A0A0A]" data-testid={`finder-selected-${asset.finderKey}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 ${positionColor(asset.position)}`}>{asset.position}</span>
                      <span className="text-sm font-semibold truncate">{asset.name}</span>
                    </div>
                    <span className="font-mono-data text-sm tabular-nums font-bold">{fmtNum(asset.combinedValue)}</span>
                    <button
                      onClick={() => removeAsset(asset.finderKey)}
                      className="text-[#52525B] hover:text-[#FF3B30] transition-colors"
                      aria-label={`Remove ${asset.name}`}
                      data-testid={`finder-remove-${asset.finderKey}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data mb-2">
              Click or check assets to include
            </div>
            <div className="max-h-[360px] overflow-auto space-y-1 pr-1">
              {rosterAssets.map((asset) => {
                const checked = selectedSet.has(asset.finderKey);
                return (
                  <button
                    key={asset.finderKey}
                    onClick={() => toggleAsset(asset)}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 border text-left transition-colors ${checked ? "border-[#0A0A0A] bg-[#F5F0FF]" : "border-dashed border-[#0A0A0A]/15 hover:border-[#0A0A0A] hover:border-solid hover:bg-white text-[#52525B] hover:text-[#0A0A0A]"}`}
                    data-testid={`finder-roster-${asset.finderKey}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAsset(asset)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-[#7C3AED]"
                        data-testid={`finder-check-${asset.finderKey}`}
                      />
                      <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(asset.position)} opacity-80`}>{asset.position}</span>
                      <span className="truncate text-xs">{asset.name}</span>
                    </div>
                    <span className="font-mono-data text-[11px] tabular-nums">{fmtNum(asset.combinedValue)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 min-w-0">
          <div className="brutal-card p-4" data-testid="trade-finder-controls">
            <label className="label-eyebrow block mb-1.5">Position Wanted</label>
            <div className="flex flex-wrap gap-1" data-testid="finder-positions">
              {POSITIONS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPosition(p)}
                  className={`px-2.5 py-1.5 text-xs uppercase tracking-widest border transition-colors ${position === p ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]"}`}
                  data-testid={`finder-pos-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {!hasPackage ? (
            <EmptyState
              icon={<Target size={28} className="mx-auto text-[#52525B] mb-2" />}
              title="Build your package to start."
              detail="Select one or more players or picks from your roster."
            />
          ) : results.length === 0 ? (
            <EmptyState
              icon={<Search size={28} className="mx-auto text-[#52525B] mb-2" />}
              title={`No ${position === "ANY" ? "players" : position} found near ${fmtNum(packageValue)}.`}
              detail="Try ANY position or widen the range slider."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="finder-results">
              {results.map(({ roster, player, value, delta, resultKey }) => (
                <ResultCard
                  key={resultKey}
                  resultKey={resultKey}
                  roster={roster}
                  player={player}
                  value={value}
                  delta={delta}
                  targetValue={packageValue}
                  selectedAssets={selectedAssets}
                  onJump={() => onJumpToTradeCalculator?.(roster, player, selectedAssets)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function EmptyState({ icon, title, detail }) {
  return (
    <div className="brutal-card p-8 text-center">
      {icon}
      <div className="font-display font-bold text-lg">{title}</div>
      <div className="text-xs text-[#52525B] mt-1 font-mono-data">{detail}</div>
    </div>
  );
}

function ResultCard({ resultKey, roster, player, value, delta, targetValue, selectedAssets, onJump }) {
  const deltaLabel = delta > 0 ? `+${fmtNum(delta)}` : fmtNum(delta);

  return (
    <div className="brutal-card" data-testid={`finder-result-${resultKey}`}>
      <div className="border-b border-[#0A0A0A] px-3 py-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="label-eyebrow truncate">{roster.team_name}</div>
          <div className="text-[10px] text-[#52525B] font-mono-data truncate">
            Owned by {roster.display_name}
          </div>
        </div>
        <div className="px-2 py-1 border border-[#0A0A0A] bg-[#FACC15] text-[10px] font-display font-bold uppercase tracking-widest whitespace-nowrap">
          {deltaLabel}
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between gap-2 border-l-4 border-l-[#7C3AED] border-y border-r border-[#0A0A0A] bg-[#F5F0FF] px-2 py-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(player.position)}`}>{player.position}</span>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{player.name}</div>
              <div className="text-[10px] text-[#52525B] font-mono-data truncate">{player.team || "—"}</div>
            </div>
          </div>
          <span className="font-mono-data text-sm tabular-nums font-bold">{fmtNum(value)}</span>
        </div>
      </div>
      <div className="border-t border-[#0A0A0A] px-3 py-2 flex items-center justify-between gap-2 bg-[#FAFAFA]">
        <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data">
          {selectedAssets.length} out · {fmtNum(targetValue)}
        </div>
        <button
          onClick={onJump}
          className="text-[10px] uppercase tracking-widest border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#7C3AED] hover:border-[#7C3AED] px-2 py-1 transition-colors inline-flex items-center gap-1"
          data-testid={`finder-jump-${resultKey}`}
        >
          <Sparkles size={11} /> Jump to Trade Calculator
        </button>
      </div>
    </div>
  );
}
